import type { CanvasProps } from '@react-three/fiber'
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { VisuallyHidden } from '@/components/ui/VisuallyHidden'
import { useInView } from '@/hooks/useInView'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { useAppStore } from '@/lib/store'
import { hasWebGL2 } from './capabilities'
import { loadSceneRenderer } from './loaders'
import { isConstrainedConnection } from './prefetch'
import { whenOnline, withRetry } from './retry'
import { queueSceneWarmup } from './warmup'
import { WebGLErrorBoundary } from './WebGLErrorBoundary'
import styles from './SceneCanvas.module.css'

const SceneRenderer = lazy(() => withRetry(loadSceneRenderer))

/** Remounts after a scene error, on top of the download retries inside `withRetry`. */
const MAX_SCENE_RETRIES = 2
/** How long a lost GPU context gets before a fresh renderer replaces it. */
const CONTEXT_REMOUNT_DELAY_MS = 800
/** A warm-up that takes longer than this stops holding up the scenes queued after it. */
const WARMUP_TIMEOUT_MS = 8000

/** Props for {@link SceneCanvas}. Anything else is passed through to R3F's `<Canvas>`. */
export interface SceneCanvasProps extends Omit<CanvasProps, 'frameloop' | 'children' | 'fallback'> {
  /** Scene id: written to `data-scene` and used for readiness (`readyScenes[id]`). */
  id: string
  /** Screen-reader description of the scene. The canvas itself is `aria-hidden`. */
  label: string
  /**
   * Still shown while the scene loads, while it recovers from an error or a lost GPU
   * context, and instead of it when WebGL2 is missing. It cross-fades out on the first frame.
   */
  fallback?: ReactNode
  /** Clears caches before a failed scene remounts, e.g. a model loader's cached error. */
  onRetry?: () => void | Promise<void>
  /**
   * Mount and draw this scene in idle time after the intro, one scene at a time, so its
   * expensive first frame never lands mid-scroll. Skipped on Save-Data/2G. Default `true`.
   */
  warmUp?: boolean
  /** Lazy-mount distance: the Canvas mounts once inside this margin. Default `'200% 0px'`. */
  mountMargin?: string
  /** Render distance: the loop runs only inside this margin. Default `'10% 0px'`. */
  renderMargin?: string
  /** Keep animating under reduced motion (instead of `frameloop="demand"`). Default `false`. */
  animateWhenReduced?: boolean
  /** Class for the outer wrapper, which fills its positioned parent by default. */
  className?: string
  /** Optional renderer tone mapping, resolved inside the lazy engine. */
  toneMapping?: 'neutral'
  /** The scene: R3F elements only. */
  children: ReactNode
}

/**
 * Lightweight scene shell: the original WebGL renderer loads only near the viewport.
 *
 * Resilience, because phones drop connections and GPU contexts:
 * - Renderer and scene chunks load through `withRetry`, so one failed download is retried.
 * - A scene that throws (a failed model fetch, a lost context during creation) remounts up
 *   to {@link MAX_SCENE_RETRIES} times with backoff, and again when the browser comes back
 *   online. `onRetry` runs first so loaders can drop their cached failure.
 * - A lost GPU context drops its renderer at once, shows the still, and mounts a fresh one.
 */
export function SceneCanvas({
  id,
  label,
  fallback = null,
  onRetry,
  warmUp = true,
  mountMargin = '200% 0px',
  renderMargin = '10% 0px',
  animateWhenReduced = false,
  className,
  children,
  ...props
}: SceneCanvasProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const mounted = useInView(rootRef, { rootMargin: mountMargin, once: true })
  const inRenderRange = useInView(rootRef, { rootMargin: renderMargin })
  const reduced = useReducedMotion()
  const menuOpen = useAppStore((s) => s.menuOpen)
  const booting = useAppStore((s) => s.phase === 'boot')
  const pageReady = useAppStore((s) => s.phase === 'ready')
  const supported = hasWebGL2()

  // Every remount gets a new attempt number. "Drawn" belongs to one attempt, so a remounted
  // renderer keeps the still up until it has put its own first frame on screen.
  const [attempt, setAttempt] = useState(0)
  const [drawnAttempt, setDrawnAttempt] = useState(-1)
  const [failed, setFailed] = useState(false)
  const [lost, setLost] = useState(false)
  // Mounted early by the idle warm-up queue, before the visitor scrolls near.
  const [warm, setWarm] = useState(false)
  const finishWarmup = useRef<(() => void) | null>(null)
  const onRetryRef = useRef(onRetry)
  useEffect(() => {
    onRetryRef.current = onRetry
  }, [onRetry])

  const remount = useCallback(async () => {
    await onRetryRef.current?.()
    setFailed(false)
    setLost(false)
    setAttempt((n) => n + 1)
  }, [])

  // A scene error: back off, wait for a connection, then remount.
  useEffect(() => {
    if (!failed || attempt >= MAX_SCENE_RETRIES) return
    let cancelled = false
    const timer = window.setTimeout(
      () => {
        void whenOnline().then(() => {
          if (!cancelled) void remount()
        })
      },
      1200 * 2 ** attempt,
    )
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [failed, attempt, remount])

  // Out of retries: one more try when the connection comes back.
  useEffect(() => {
    if (!failed || attempt < MAX_SCENE_RETRIES) return
    const retry = () => void remount()
    window.addEventListener('online', retry, { once: true })
    return () => window.removeEventListener('online', retry)
  }, [failed, attempt, remount])

  // A lost context: the dead renderer is already unmounted (cleanup calls on a lost
  // context are no-ops, so nothing warns). Give the GPU a moment, then build a fresh one.
  useEffect(() => {
    if (!lost) return
    const timer = window.setTimeout(() => void remount(), CONTEXT_REMOUNT_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [lost, remount])

  const handleError = useCallback(() => setFailed(true), [])
  const handleLost = useCallback(() => setLost(true), [])
  const handleReady = useCallback(() => setDrawnAttempt(attempt), [attempt])

  const live = supported && drawnAttempt === attempt && !failed && !lost

  // After the intro, join the idle warm-up queue unless the visitor already got here.
  useEffect(() => {
    if (!warmUp || !pageReady || !supported || mounted || warm || isConstrainedConnection()) return
    return queueSceneWarmup(
      () =>
        new Promise<void>((resolve) => {
          finishWarmup.current = resolve
          setWarm(true)
          window.setTimeout(resolve, WARMUP_TIMEOUT_MS)
        }),
    )
  }, [warmUp, pageReady, supported, mounted, warm])

  // The first frame is drawn: let the next queued scene start.
  useEffect(() => {
    if (!live) return
    finishWarmup.current?.()
    finishWarmup.current = null
  }, [live])
  const frameloop = menuOpen
    ? 'never'
    : booting || (reduced && !animateWhenReduced)
      ? 'demand'
      : inRenderRange
        ? 'always'
        : 'never'

  return (
    <div
      ref={rootRef}
      className={cx(styles.root, className)}
      data-scene={id}
      data-renderer={supported ? 'webgl' : 'unavailable'}
      data-state={
        !supported ? 'unsupported' : failed ? 'failed' : lost ? 'lost' : live ? 'live' : 'loading'
      }
      data-active={inRenderRange && !menuOpen && !booting}
    >
      <div className={styles.canvas} aria-hidden="true">
        {supported && (mounted || warm) && !failed && !lost ? (
          <WebGLErrorBoundary key={attempt} label={label} onError={handleError}>
            <Suspense fallback={null}>
              <SceneRenderer
                {...props}
                id={id}
                frameloop={frameloop}
                onReady={handleReady}
                onContextLost={handleLost}
              >
                {children}
              </SceneRenderer>
            </Suspense>
          </WebGLErrorBoundary>
        ) : null}
        {fallback ? (
          <div className={styles.fallback} data-visible={!live}>
            {fallback}
          </div>
        ) : null}
      </div>
      <VisuallyHidden as="p">{label}</VisuallyHidden>
    </div>
  )
}
